import { mapSupportDescription } from "@/app/common-utils/question/map-support-description";
import { MagnoliaImage } from "@/components/atoms/image/image";
import { PRODUCT, PRODUCT_DOMAIN } from "@/constants";

import {
  CoverTypePageContent,
  GroupedCoverTypeMagnoliaDataType,
} from "../types/cover-type.type";

export function createImageObject(imageUrl: string): MagnoliaImage {
  return {
    "@id": "",
    "@link": imageUrl,
    "@name": "",
    "@path": "",
    "@type": "",
    "@uuid": "",
  };
}

// These functions will be used inside groupedCoverTypeMagnoliaData
function createBackgroundImage(
  imageUrl: string | MagnoliaImage | undefined | null
): MagnoliaImage | undefined | null {
  if (typeof imageUrl === "string") {
    return {
      "@id": "",
      "@link": imageUrl,
      "@name": "",
      "@path": "",
      "@type": "",
      "@uuid": "",
    };
  }
  return imageUrl;
}

export function groupedCoverTypeMagnoliaData(
  magnoliaData: CoverTypePageContent
) {
  const magnoliaDataObj = magnoliaData.overlayArea[0];

  const img = createBackgroundImage(magnoliaDataObj.backgroundImageDesktop);

  const groupedData: GroupedCoverTypeMagnoliaDataType = {
    1: {
      headerText: magnoliaDataObj.ctaButtonOneLabel ?? "",
      descriptionSection: {
        title: magnoliaDataObj.answerOneDescription?.[0]?.title ?? "",
        body:
          mapSupportDescription(
            magnoliaDataObj.answerOneDescription?.[0]?.questionBody
          ) ?? "",
        isLoadMoreButton:
          magnoliaDataObj.loadMoreButtonTextST !== undefined &&
          magnoliaDataObj.loadMoreButtonTextST !== "",
        loadMoreButtonText: magnoliaDataObj.loadMoreButtonTextST ?? "",
        drawerData: magnoliaDataObj.supportDrawerST ?? {},
      },
    },

    2: {
      headerText: magnoliaDataObj.ctaButtonTwoLabel ?? "",
      descriptionSection: {
        title: magnoliaDataObj.answerTwoDescription?.[0]?.title ?? "",
        body:
          mapSupportDescription(
            magnoliaDataObj.answerTwoDescription?.[0]?.questionBody
          ) ?? "",
        isLoadMoreButton:
          magnoliaDataObj.loadMoreButtonTextAMT !== undefined &&
          magnoliaDataObj.loadMoreButtonTextAMT !== "",
        loadMoreButtonText: magnoliaDataObj.loadMoreButtonTextAMT ?? "",
        drawerData: magnoliaDataObj.supportDrawerAMT ?? {},
      },
    },
    0: {
      headerText: magnoliaDataObj.questionText ?? "",
      descriptionSection: {
        title:
          magnoliaDataObj.assistantDescription?.[0]?.userHeaderSection?.[0]
            ?.text ?? "",
        body:
          magnoliaDataObj.assistantDescription?.[0]?.userHeaderSection?.["00"]
            ?.text ?? "",
        isLoadMoreButton: false,
        loadMoreButtonText: "",
        trustpilotDescription: magnoliaDataObj.trustpilotDescription ?? "",
        brandDomain: magnoliaDataObj.brandDomain ?? "",
        trustpilotSupportCopyText:
          magnoliaDataObj.trustpilotSupportCopyText ?? "",
        navigateUrl: magnoliaDataObj.navigateUrl ?? "",
        backGroundImage: img ? img["@link"] : undefined,
        drawerData: {},
      },
    },
  } as const;
  return groupedData;
}

export function getSingleOptionItem(
  id: 0 | 1 | 2,
  selectedAnswer: 0 | 1 | 2,
  cmsGroupedData: GroupedCoverTypeMagnoliaDataType
) {
  const optionMap: Record<
    0 | 1 | 2,
    { labelValue: string; isSelected: boolean }
  > = {
    0: { labelValue: "", isSelected: selectedAnswer === 0 },
    1: {
      labelValue: cmsGroupedData[id].headerText,
      isSelected: selectedAnswer === 1,
    },
    2: {
      labelValue: cmsGroupedData[id].headerText,
      isSelected: selectedAnswer === 2,
    },
  };
  return {
    id,
    ...optionMap[id],
  };
}

const classNames = {
  avanti: {
    toggleBtnText: " text-text-title ",
  },
  staysure: {
    toggleBtnText: " text-functional ",
  },
};

const brand: "staysure" | "avanti" =
  (PRODUCT as "staysure" | "avanti") ?? "staysure";

const brandClassNames = classNames[brand];

export default brandClassNames;

const BRAND_LOCALE_MAP: Record<string, string> = {
  ESP: "es-ES",
  EXP: "en",
  IRE: "en",
};

const getLocaleByBrand = (brand?: string): string => {
  return BRAND_LOCALE_MAP[brand ?? ""] ?? "en";
};

// Formats Trustpilot star rating value (ESP): "4,7" (comma), Others (EXP, IRE, STS, AVN): "4.8" (dot)
export const formatTrustpilotRating = (rating: number): string => {
  if (!Number.isFinite(rating)) return "0.0";

  const locale = getLocaleByBrand(PRODUCT_DOMAIN);

  return rating.toLocaleString(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
};

// Formats Trustpilot review count with thousand separators
export const formatTrustpilotReviewCount = (count: string | number): string => {
  const numCount = typeof count === "string" ? parseInt(count, 10) : count;
  if (Number.isNaN(numCount)) return "0";

  return numCount.toLocaleString("en", {
    useGrouping: true,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};
